import { useState } from "react";
import { Music2, ArrowUpRight } from "lucide-react";
import { POETRY } from "../../data/content";
import { Reveal, Eyebrow } from "./Reveal";

const Bars = () => (
  <span className="flex h-5 items-end gap-[3px]" aria-hidden="true">
    {[0.9, 0.55, 1.15, 0.7, 1.3, 0.62].map((d, i) => (
      <span
        key={i}
        className="eq-bar h-full w-[3px] rounded-full bg-[#cbb890]"
        style={{ animationDuration: `${d}s`, animationDelay: `${i * 0.12}s` }}
      />
    ))}
  </span>
);

export default function Poetry() {
  const [active, setActive] = useState(0);

  return (
    <section
      id="poetry"
      data-testid="poetry-section"
      className="border-y border-[#0b1320] bg-[#0f1e36]"
    >
      <div className="mx-auto max-w-[1400px] px-6 py-28 md:px-10 md:py-40">
        <div className="grid gap-14 lg:grid-cols-12 lg:gap-16">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <Eyebrow testid="poetry-eyebrow" tone="dark">
                  {POETRY.eyebrow}
                </Eyebrow>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-7 font-display text-4xl font-light leading-[1.02] tracking-tight text-[#f8fafc] md:text-5xl lg:text-6xl">
                  {POETRY.heading}
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-7 max-w-md text-base leading-relaxed text-[#aab4c8] md:text-lg">
                  {POETRY.intro}
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-9 flex flex-wrap gap-3">
                  {POETRY.platforms.map((p) => (
                    <a
                      key={p.name}
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`poetry-platform-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="group inline-flex items-center gap-2 rounded-full bg-[#cbb890] px-5 py-3 text-sm text-[#0f1e36] transition-colors duration-300 hover:bg-[#f8fafc]"
                    >
                      <Music2 className="h-4 w-4" />
                      {p.name}
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </a>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.25}>
                <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8b95ab]">
                  Listening Room — Wingwomen Omnimedia
                </p>
              </Reveal>
            </div>
          </div>

          {/* Right — tracklist deck */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-sm border border-white/10 bg-white/[0.03]">
                <div className="flex items-center justify-between px-6 py-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#cbb890]">
                    Tracklist
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8b95ab]">
                    03 Recordings
                  </span>
                </div>
                {POETRY.works.map((w, i) => (
                  <button
                    key={w.no}
                    type="button"
                    data-testid={`poetry-work-${w.no}`}
                    onClick={() => setActive(i)}
                    className={`group flex w-full items-center gap-6 border-t border-white/10 px-6 py-7 text-left transition-colors duration-300 ${
                      active === i ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"
                    }`}
                  >
                    <span className="font-mono text-xs text-[#cbb890]">{w.no}</span>
                    <span className="flex-1">
                      <span
                        className={`block font-display text-2xl leading-tight transition-colors duration-300 md:text-3xl ${
                          active === i ? "text-[#cbb890]" : "text-[#f8fafc]"
                        }`}
                      >
                        {w.title}
                      </span>
                      <span className="mt-1 block text-xs uppercase tracking-[0.14em] text-[#8b95ab]">
                        {w.meta}
                      </span>
                    </span>
                    {active === i ? (
                      <Bars />
                    ) : (
                      <Music2 className="h-4 w-4 shrink-0 text-[#8b95ab] transition-colors group-hover:text-[#cbb890]" />
                    )}
                  </button>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8b95ab]">
                Select a track, then stream it on your platform of choice.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
